import { inject, Injectable } from '@angular/core';
import { catchError, map, of, switchMap, Observable } from 'rxjs';

import { CurrentSiteState } from '@ppg/configuration';
import { ClientSiteSettingsKeys } from '@ppg/core/enums';
import { LanguagesService } from '@ppg/core/language';
import { FeatureFlagService } from '@ppg/shared/feature-flag';

import { ProductColorDetailsQuery } from '../queries/product-color-details.query';
import { ProductsSkuQuery } from '../queries/products-sku.query';
import { TintedColorBasesQuery } from '../queries/tinted-color-bases.query';
import { TintedColorsQuery } from '../queries/tinted-colors.query';
import {
  getContextClientName,
  getContextClientNameIfAnonymousTransactable,
} from '../utils/get-context-client-name.util';

@Injectable({
  providedIn: 'root',
})
export class TintedColorService {
  #colorsQuery = inject(TintedColorsQuery);
  #colorBasesQuery = inject(TintedColorBasesQuery);
  #languageService = inject(LanguagesService);
  #featureFlagService = inject(FeatureFlagService);
  #productsSkuQuery = inject(ProductsSkuQuery);
  #productColorDetailsQuery = inject(ProductColorDetailsQuery);
  #currentSiteState = inject(CurrentSiteState);

  readonly isProductDetailsAnonymous = this.#currentSiteState.getSiteFlag(
    ClientSiteSettingsKeys.AnonymousJourneyProductDetailsAnonymousEnabled,
  );

  private readonly globalSettings$ = this.#featureFlagService.getGlobalSettingsAsAnonymous(
    this.#languageService.language(),
  );

  getTintedColors(productGroupId: string, searchString: string) {
    return this.globalSettings$.pipe(
      switchMap((globalSettings) =>
        this.#colorsQuery.fetch(
          {
            languageCode: this.#languageService.languageCode(),
            searchString,
            productGroupId,
          },
          getContextClientNameIfAnonymousTransactable(globalSettings, this.isProductDetailsAnonymous),
        ),
      ),
      map((response) => response.data.colors),
      catchError(() => of([])),
    );
  }

  getColorBases(productGroupId: string, colorCode: string, paletteCode: string, colorId?: string) {
    return this.globalSettings$.pipe(
      switchMap((globalSettings) =>
        this.#colorBasesQuery.fetch(
          {
            languageCode: this.#languageService.languageCode(),
            productGroupId,
            colorCode,
            paletteCode,
            colorId,
          },
          getContextClientNameIfAnonymousTransactable(globalSettings, this.isProductDetailsAnonymous),
        ),
      ),
      map((response) => response.data.colorBases),
      catchError(() => of([])),
    );
  }

  getProductsSku(colorCode: string): Observable<string[]> {
    if (!colorCode) {
      return of([]);
    }

    return this.globalSettings$.pipe(
      switchMap((globalSettings) =>
        this.#productsSkuQuery.fetch(
          {
            languageCode: this.#languageService.languageCode(),
            productSkuSearch: {
              colorCode: colorCode,
            },
          },
          getContextClientName(globalSettings),
        ),
      ),
      map((response) => response.data.productSKUByColor.map((x) => x.baseMaterialNumber)),
      catchError(() => of([])),
    );
  }

  getProductColorDetails(productGroupId: string, colorCode: string, colorPalette: string) {
    return this.globalSettings$.pipe(
      switchMap((globalSettings) =>
        this.#productColorDetailsQuery.fetch(
          {
            productGroupId,
            languageCode: this.#languageService.languageCode(),
            colorCode,
            colorPalette,
          },
          {
            errorPolicy: 'all',
            ...getContextClientNameIfAnonymousTransactable(globalSettings, this.isProductDetailsAnonymous),
          },
        ),
      ),
      map((response) => response.data.color),
      catchError(() => of(undefined)),
    );
  }
}
